<template>
  <v-container :class="containerClass" fluid>
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="isLoading"
          :options.sync="options"
          :server-items-length="itemList.meta.total"
          :footer-props="defaultDataTableFooterProps"
        >
          <template #top>
            <v-sheet>
              <v-container fluid class="pa-4">
                <v-row align="center">
                  <v-col md="6" cols="12">
                    <h2>Study Report List</h2>
                  </v-col>
                  <v-col md="6" cols="12" class="text-right">
                    <v-btn
                      class="mx-1"
                      :small="$vuetify.breakpoint.mobile"
                      @click="showFilters = !showFilters"
                    >
                      <v-icon :small="$vuetify.breakpoint.mobile" left
                        >mdi-filter</v-icon
                      >{{ showFilters ? 'Hide' : 'Show' }} Filter
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-sheet>
            <v-divider></v-divider>
            <v-expand-transition>
              <v-sheet v-show="showFilters" color="grey lighten-5">
                <v-container fluid class="pa-4">
                  <v-row>
                    <v-col v-if="!isStudentAccount" md="2" cols="12">
                      <div class="label mb-2">Student</div>
                      <v-autocomplete
                        v-model="filters.student_id"
                        :items="students"
                        item-text="name"
                        item-value="id"
                        :loading="isStudentListLoading"
                        :search-input.sync="search.student_name"
                        outlined
                        solo
                        flat
                        background-color="white"
                        label="Student"
                        hide-details
                        dense
                        clearable
                        cache-items
                        @change="getListData"
                        @click:clear="getListData"
                      />
                    </v-col>
                    <v-col md="2" cols="12">
                      <div class="label mb-2">Classroom</div>
                      <v-autocomplete
                        v-model="filters.classroom_id"
                        :items="classrooms"
                        item-text="name"
                        item-value="id"
                        :loading="isClassroomListLoading"
                        :search-input.sync="search.classroom_name"
                        outlined
                        solo
                        flat
                        background-color="white"
                        label="Classroom"
                        hide-details
                        dense
                        clearable
                        @change="getListData"
                        @click:clear="getListData"
                      />
                    </v-col>
                    <v-col v-if="!isTeacherAccount" md="2" cols="12">
                      <div class="label mb-2">Teacher</div>
                      <v-autocomplete
                        v-model="filters.teacher_id"
                        :items="teachers"
                        item-text="name"
                        item-value="id"
                        :loading="isTeacherListLoading"
                        :search-input.sync="search.teacher_name"
                        outlined
                        solo
                        flat
                        background-color="white"
                        label="Teacher"
                        hide-details
                        dense
                        clearable
                        @change="getListData"
                        @click:clear="getListData"
                      />
                    </v-col>
                    <v-col md="2" cols="12">
                      <div class="label mb-2">Term</div>
                      <v-text-field
                        v-model="filters.term"
                        outlined
                        solo
                        flat
                        background-color="white"
                        label="Term"
                        hide-details
                        dense
                        clearable
                        type="number"
                        @change="getListData"
                        @blur="getListData"
                        @click:clear="getListData"
                      />
                    </v-col>
                    <v-col md="2" cols="12">
                      <div class="label mb-2">Month</div>
                      <v-text-field
                        v-model="filters.month"
                        outlined
                        solo
                        flat
                        background-color="white"
                        label="Month"
                        hide-details
                        dense
                        clearable
                        type="number"
                        @change="getListData"
                        @blur="getListData"
                        @click:clear="getListData"
                      />
                    </v-col>
                  </v-row>
                </v-container>
                <v-divider></v-divider>
              </v-sheet>
            </v-expand-transition>
          </template>
          <template #[`item.action`]="{ item }">
            <div class="text-right">
              <v-btn
                depressed
                color="success"
                :href="
                  $route('study-progress.monthly-reports.download-pdf', {
                    classroom_id: item.classroom_id,
                    student_id: item.student_id,
                    term: item.term,
                    month: item.month,
                  })
                "
                target="_blank"
              >
                <v-icon left>mdi-cloud-download</v-icon>Download
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import LayoutDashboard from '@layouts/Dashboard'
import { ListParams } from '@/models'
import utils from '@/utils'

export default {
  name: 'StudyReport',
  layout: LayoutDashboard,
  data() {
    return {
      showFilters: true,
      filters: {
        classroom_id: null,
        student_id: null,
        teacher_id: null,
        term: null,
        month: null,
      },
      search: {
        student_name: null,
        teacher_name: null,
        classroom_name: null,
      },
      options: {
        page: 1,
        itemsPerPage: 10,
      },
    }
  },
  computed: {
    ...mapGetters({
      authUser: 'authUser',
      itemList: 'studyProgress/monthlyReportList',
      isLoading: 'studyProgress/isMonthlyReportListLoading',
      studentList: 'student/searchList',
      isStudentListLoading: 'student/isSearchListLoading',
      teacherList: 'teacher/searchList',
      isTeacherListLoading: 'teacher/isSearchListLoading',
      classroomList: 'classroom/searchList',
      isClassroomListLoading: 'classroom/isSearchListLoading',
    }),
    headers() {
      return [
        ...(this.isStudentAccount === false
          ? [{ text: 'Student', value: 'student_name', sortable: false }]
          : []),
        { text: 'Class', value: 'classroom_name', sortable: false },
        ...(this.isTeacherAccount === false
          ? [{ text: 'Teacher', value: 'teacher_name', sortable: false }]
          : []),
        { text: 'Term', value: 'term', sortable: false },
        { text: 'Month', value: 'month', sortable: false },
        { text: 'Action', value: 'action', sortable: false, align: 'center' },
      ]
    },
    items() {
      return this.itemList.data
    },
    classrooms() {
      return this.classroomList.data
    },
    students() {
      return this.studentList.data
    },
    teachers() {
      return this.teacherList.data
    },
  },
  watch: {
    'search.student_name': function (newVal, oldVal) {
      if (newVal && newVal != oldVal) {
        this.debouncedSearchStudentList()
      }
    },
    'search.teacher_name': function (newVal, oldVal) {
      if (newVal && newVal != oldVal) {
        this.debouncedSearchTeacherList()
      }
    },
    'search.classroom_name': function (newVal, oldVal) {
      if (newVal && newVal != oldVal) {
        this.debouncedSearchClassroomList()
      }
    },
    // searchInputStudent(val) {
    //   if (val) {
    //     this.debouncedSearchStudentList()
    //   }
    // },
  },
  created() {
    this.init()
    this.debouncedSearchStudentList = _.debounce(this.searchStudentList, 500)
    this.debouncedSearchTeacherList = _.debounce(this.searchTeacherList, 500)
    this.debouncedSearchClassroomList = _.debounce(
      this.searchClassroomList,
      500
    )
  },
  methods: {
    ...mapActions({
      getItemList: 'studyProgress/getMonthlyReportList',
      getTeacherList: 'teacher/search',
      getStudentList: 'student/search',
      getClassroomList: 'classroom/search',
    }),
    async init() {
      if (this.isTeacherAccount) {
        this.filters.teacher_id = this.authUser.account_id
        // this.getClassroomList({
        //   teacher_id: this.authUser.account_id,
        // });
      } else if (this.isStudentAccount) {
        this.filters.student_id = this.authUser.account_id
        // this.getClassroomList({
        //   student_id: this.authUser.account_id,
        // });
        // this.getTeacherList();
      }

      await this.getListData()
    },
    getListData() {
      const listParams = new ListParams(this.filters, this.options)
      const request = utils.toServerSideQuery(listParams)
      this.getItemList(request)
    },
    searchStudentList() {
      this.getStudentList({
        search: this.search.student_name,
        filters: {
          ...(this.isTeacherAccount && {
            teacher_id: this.authUser.account_id,
          }),
        },
      })
    },
    searchTeacherList() {
      this.getTeacherList({
        search: this.search.teacher_name,
        filters: {
          ...(this.isStudentAccount && {
            student_id: this.authUser.account_id,
          }),
        },
      })
    },
    searchClassroomList() {
      this.getClassroomList({
        search: this.search.classroom_name,
        filters: {
          ...(this.isStudentAccount && {
            student_id: this.authUser.account_id,
          }),
          ...(this.isTeacherAccount && {
            teacher_id: this.authUser.account_id,
          }),
        },
      })
    },
  },
}
</script>
